import React, { useEffect, useState } from "react";
import { DoubleStructure } from "../core/DoubleStructure/DoubleStructure";
import { BodyTyp } from "../Typography/Typography";
import {
  AlignmentEnum,
  FilterProps,
  FontWeightEnum,
} from "../../types/AppTypes";
import { ReactComponent as CloseIcon } from "../../icons/close.svg";
import { ReactComponent as CheckIcon } from "../../icons/check.svg";
import { ReactComponent as FilterIcon } from "../../icons/filters.svg";
import {
  CONFIRM_LABEL,
  FILTERS_LABEL,
  RESET_FILTERS_LABEL,
} from "../../constants/Dictionary";
import { ButtonVariants, ButtonSizes, Button } from "../core/Button/Button";
import { FiltersTitles } from "../../constants/FilterTitles";
import { FiltersContent } from "../FiltersContent/FiltersContent";
import { Link, LinkSizeEnum } from "../core/Link/Link";
import { SidePanel } from "../core/SidePanel/SidePanel";
import { Loader } from "../core/Loader/Loader";

type FiltersPanelProps<T> = {
  data: T | null;
  selectedFilters: FilterProps[];
  setSelectedFilters: (filters: FilterProps[]) => void;
  filtersTitles?: FiltersTitles[];
  fetchFilters?: () => Promise<void>;
  loading?: boolean;
  defaultExpanded?: boolean;
};

export type FilterUpdate = {
  value: string | number | (string | number)[] | null;
  key: string;
};

export const FiltersPanel = <T,>({
  data,
  selectedFilters,
  setSelectedFilters,
  filtersTitles,
  fetchFilters,
  loading,
  defaultExpanded,
}: FiltersPanelProps<T>) => {
  const [filters, setFilters] = useState<T | null>(null);
  const [tmpSelectedFilters, setTmpSelectedFilters] =
    useState<FilterProps[]>(selectedFilters);
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    setTmpSelectedFilters(selectedFilters);
    setFilters(data);
  }, [selectedFilters, showFilters]);

  useEffect(() => {
    if (data) {
      setFilters(data);
    }
  }, [data]);

  const handleFilter = (filtersUpdates: FilterUpdate[]) => {
    let newFilters: FilterProps[] = structuredClone(tmpSelectedFilters);

    for (const filterUpdate of filtersUpdates) {
      const { value, key } = filterUpdate;

      const existElement = newFilters.filter((item) => item.key === key);
      if (existElement.length > 0) {
        if (value !== undefined && value !== null) {
          const valueArray = existElement[0].value;
          let isPresent = false;
          if (Array.isArray(value)) {
            isPresent = true;
          } else {
            isPresent = valueArray.includes(value);
          }
          const filtered = newFilters.filter((item) => item.key !== key);

          let newValue: (string | number)[] = [];

          if (isPresent) {
            if (Array.isArray(value)) {
              newValue = value;
            } else {
              newValue = valueArray.filter((val) => val !== value);
            }
          } else {
            if (!Array.isArray(value)) {
              newValue = [value];
            }
          }

          const obj = { value: newValue, key: key };
          newFilters = newValue.length > 0 ? [...filtered, obj] : [...filtered];
        } else {
          const filtered = newFilters.filter((item) => item.key !== key);
          newFilters = [...filtered];
        }
      } else if (value !== undefined && value !== null) {
        const obj = { value: Array.isArray(value) ? value : [value], key: key };
        newFilters = [...newFilters, obj];
      }
    }
    setTmpSelectedFilters(newFilters);
  };

  const saveFilters = () => {
    const newFilters = JSON.parse(JSON.stringify(tmpSelectedFilters));
    setSelectedFilters(newFilters);
    setShowFilters(false);
  };

  const clearFilters = () => {
    setTmpSelectedFilters([]);
  };

  const onClose = () => {
    setTmpSelectedFilters(selectedFilters);
    setShowFilters(false);
  };

  const handleOpenFilters = () => {
    if (fetchFilters) {
      fetchFilters();
    }
    setShowFilters(true);
  };

  return (
    <>
      <Button
        label={FILTERS_LABEL}
        onClick={handleOpenFilters}
        variant={ButtonVariants.SECONDARY}
        size={ButtonSizes.REGULAR}
        icon={<FilterIcon />}
      />
      <SidePanel
        label={FILTERS_LABEL}
        onClose={onClose}
        visible={showFilters}
        footer={
          <DoubleStructure verticalAlignment={AlignmentEnum.CENTER}>
            <Link
              onClick={clearFilters}
              icon={<CloseIcon />}
              size={LinkSizeEnum.SMALL}
              disabled={tmpSelectedFilters.length === 0 || loading}
            >
              <BodyTyp variant="1" fontWeight={FontWeightEnum.MEDIUM}>
                {RESET_FILTERS_LABEL}
                {tmpSelectedFilters.length > 0 &&
                  ` (${tmpSelectedFilters.length})`}
              </BodyTyp>
            </Link>
            <Button
              label={CONFIRM_LABEL}
              onClick={saveFilters}
              variant={ButtonVariants.PRIMARY}
              size={ButtonSizes.REGULAR}
              icon={<CheckIcon />}
              disabled={
                selectedFilters.length === 0 && tmpSelectedFilters.length === 0
              }
            />
          </DoubleStructure>
        }
      >
        {filters && showFilters && (
          <Loader loading={loading ?? false} className="panel-loader">
            <FiltersContent<T>
              tmpFilters={tmpSelectedFilters}
              handleFilter={handleFilter}
              filtersTitles={filtersTitles}
              data={filters}
              defaultExpanded={defaultExpanded}
            />
          </Loader>
        )}
      </SidePanel>
    </>
  );
};
